import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { ExternalLink } from 'lucide-react';

const ViewOrderModal = ({ order, isOpen, onClose }) => {
  if (!order) return null;

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getPaymentColor = (status) => {
    const colors = {
      pending: 'bg-gray-100 text-gray-800',
      paid: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Playfair Display' }}>
            Order Details
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Order Summary */}
          <div className="bg-gradient-to-r from-cyan-50 to-teal-50 rounded-lg p-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-gray-600">Order ID</Label>
                <p className="font-mono text-sm font-medium">{order.id}</p>
              </div>
              <div>
                <Label className="text-xs text-gray-600">Plan</Label>
                <p className="text-sm font-semibold">{order.plan_name}</p>
              </div>
              <div>
                <Label className="text-xs text-gray-600">Amount</Label>
                <p className="text-lg font-bold" style={{ color: '#4AC5DB' }}>${order.amount.toFixed(2)}</p>
              </div>
              <div>
                <Label className="text-xs text-gray-600">Backlinks</Label>
                <p className="text-sm font-medium">{order.backlinks} publications</p>
              </div>
              <div>
                <Label className="text-xs text-gray-600">Order Status</Label>
                <div className="mt-1">
                  <Badge className={getStatusColor(order.status)}>{order.status}</Badge>
                </div>
              </div>
              <div>
                <Label className="text-xs text-gray-600">Payment Status</Label>
                <div className="mt-1">
                  <Badge className={getPaymentColor(order.payment_status)}>{order.payment_status}</Badge>
                </div>
              </div>
            </div>
          </div>

          {/* Customer Information */}
          <div>
            <h3 className="font-semibold text-lg mb-3 flex items-center">
              <span className="w-1 h-5 bg-cyan-500 mr-2"></span>
              Customer Information
            </h3>
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <div>
                <Label className="text-xs text-gray-600">Name</Label>
                <p className="text-sm font-medium">{order.user_name}</p>
              </div>
              <div>
                <Label className="text-xs text-gray-600">Email</Label>
                <p className="text-sm font-medium">{order.user_email}</p>
              </div>
              <div>
                <Label className="text-xs text-gray-600">Order Date</Label>
                <p className="text-sm font-medium">{new Date(order.created_at).toLocaleString()}</p>
              </div>
            </div>
          </div>

          {/* Order Requirements */}
          <div>
            <h3 className="font-semibold text-lg mb-3 flex items-center">
              <span className="w-1 h-5 bg-cyan-500 mr-2"></span>
              Order Requirements
            </h3>
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <div>
                <Label className="text-xs text-gray-600">Target Website URL</Label>
                <div className="flex items-center gap-2 mt-1">
                  <p className="text-sm font-medium break-all flex-1">
                    {order.order_details?.website_url || 'Not provided'}
                  </p>
                  {order.order_details?.website_url && (
                    <a
                      href={order.order_details.website_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-cyan-600 hover:text-cyan-700"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  )}
                </div>
              </div>

              <div>
                <Label className="text-xs text-gray-600">Target Keywords ({order.order_details?.keywords?.length || 0})</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {order.order_details?.keywords?.map((keyword, idx) => (
                    <span key={idx} className="text-sm bg-cyan-100 text-cyan-800 px-3 py-1 rounded-full font-medium">
                      {keyword}
                    </span>
                  ))}
                </div>
              </div>

              {order.order_details?.image_url && (
                <div>
                  <Label className="text-xs text-gray-600">Image URL</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <p className="text-sm font-medium break-all flex-1">{order.order_details.image_url}</p>
                    <a
                      href={order.order_details.image_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-cyan-600 hover:text-cyan-700"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  </div>
                </div>
              )}

              {order.order_details?.youtube_url && (
                <div>
                  <Label className="text-xs text-gray-600">YouTube Video URL</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <p className="text-sm font-medium break-all flex-1">{order.order_details.youtube_url}</p>
                    <a
                      href={order.order_details.youtube_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-cyan-600 hover:text-cyan-700"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  </div>
                </div>
              )}

              {order.order_details?.article_content && (
                <div>
                  <Label className="text-xs text-gray-600">Article Content</Label>
                  <div className="mt-1 bg-white p-3 rounded border max-h-48 overflow-y-auto">
                    <p className="text-sm whitespace-pre-wrap">{order.order_details.article_content}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Backlinks */}
          {order.backlinks_data && order.backlinks_data.length > 0 && (
            <div>
              <h3 className="font-semibold text-lg mb-3 flex items-center">
                <span className="w-1 h-5 bg-cyan-500 mr-2"></span>
                Publications ({order.backlinks_data.length})
              </h3>
              <div className="space-y-3">
                {order.backlinks_data.map((backlink, idx) => (
                  <div key={idx} className="bg-gray-50 rounded-lg p-3 border">
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-xs font-semibold text-gray-500">Backlink {idx + 1}</span>
                      <Badge className={backlink.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                        {backlink.status}
                      </Badge>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-start gap-2">
                        <Label className="text-xs text-gray-600 w-16 mt-1">URL:</Label>
                        <div className="flex-1">
                          <a
                            href={backlink.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-cyan-600 hover:text-cyan-700 break-all inline-flex items-center gap-1"
                          >
                            {backlink.url}
                            <ExternalLink className="h-3 w-3 flex-shrink-0" />
                          </a>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Label className="text-xs text-gray-600 w-16">Published:</Label>
                        <p className="text-sm">{backlink.published_date ? new Date(backlink.published_date).toLocaleDateString() : 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Payment Information */}
          {order.payment_id && (
            <div>
              <h3 className="font-semibold text-lg mb-3 flex items-center">
                <span className="w-1 h-5 bg-cyan-500 mr-2"></span>
                Payment Information
              </h3>
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div>
                  <Label className="text-xs text-gray-600">Payment ID</Label>
                  <p className="font-mono text-sm font-medium">{order.payment_id}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end pt-4 border-t">
          <Button onClick={onClose} style={{ backgroundColor: '#4AC5DB' }} className="hover:opacity-90">
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ViewOrderModal;
